import React from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalPages.css';

function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      {/* Top Navigation Bar */}
      <div className="legal-navbar">
        <div className="legal-navbar-container">
          <div className="legal-navbar-logo" onClick={() => navigate('/')}>
            <img src="/logo.svg" alt="S.C.A.L.P.E.L." />
          </div>
        </div>
      </div>

      <div className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: December 30, 2025</p>

        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            DocPlix Solutions Private Limited ("we," "us," or "our") operates S.C.A.L.P.E.L., an AI-powered medical
            information chatbot. This Privacy Policy explains how we collect, use, disclose, and safeguard your
            information when you use our service.
          </p>
          <p>
            We are committed to protecting your privacy and ensuring the security of your personal information.
            Please read this Privacy Policy carefully.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Information We Collect</h2>

          <h3>2.1 Personal Information</h3>
          <p>We may collect the following personal information:</p>
          <ul>
            <li>Name and professional credentials</li>
            <li>Email address</li>
            <li>Contact information</li>
            <li>Medical specialty and practice information</li>
            <li>Account credentials</li>
          </ul>

          <h3>2.2 Usage Information</h3>
          <p>We automatically collect certain information when you use S.C.A.L.P.E.L.:</p>
          <ul>
            <li>Chat history and queries</li>
            <li>Device information (IP address, browser type, operating system)</li>
            <li>Usage patterns and preferences</li>
            <li>Log data and analytics</li>
          </ul>

          <h3>2.3 Medical Information</h3>
          <p>
            The queries and conversations you have with S.C.A.L.P.E.L. may contain medical information. We treat all such
            information with the highest level of confidentiality and security.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use the collected information for the following purposes:</p>
          <ul>
            <li>To provide and maintain our service</li>
            <li>To improve and personalize your experience</li>
            <li>To analyze usage patterns and improve our AI models</li>
            <li>To communicate with you about service updates and support</li>
            <li>To ensure compliance with legal obligations</li>
            <li>To detect, prevent, and address technical issues or fraudulent activity</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your personal
            information against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security assessments and updates</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Secure data storage and backup procedures</li>
            <li>Employee training on data privacy and security</li>
          </ul>
          <p>
            However, no method of transmission over the Internet or electronic storage is 100% secure. While we
            strive to use commercially acceptable means to protect your information, we cannot guarantee absolute
            security.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Data Sharing and Disclosure</h2>
          <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>
          <ul>
            <li><strong>Service Providers:</strong> With third-party vendors who assist us in operating our service</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
          </ul>
          <p>
            All third-party service providers are contractually bound to maintain the confidentiality and security
            of your information.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Data Retention</h2>
          <p>
            We retain your personal information only for as long as necessary to fulfill the purposes outlined in
            this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer
            need your information, we will securely delete or anonymize it.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Your Rights</h2>
          <p>You have the following rights regarding your personal information:</p>
          <ul>
            <li><strong>Access:</strong> Request access to your personal information</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
            <li><strong>Objection:</strong> Object to certain processing of your information</li>
            <li><strong>Withdrawal:</strong> Withdraw consent where processing is based on consent</li>
          </ul>
          <p>
            To exercise these rights, please contact us at <a href="mailto:contact@docplix.com">contact@docplix.com</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to enhance your experience, analyze usage patterns,
            and improve our service. You can control cookie settings through your browser preferences.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Children's Privacy</h2>
          <p>
            S.C.A.L.P.E.L. is not intended for use by individuals under the age of 18. We do not knowingly collect personal
            information from children. If we become aware that we have collected information from a child, we will
            take steps to delete such information.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your country of residence.
            We ensure that appropriate safeguards are in place to protect your information in accordance with this
            Privacy Policy.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by
            posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use
            of S.C.A.L.P.E.L. after such changes constitutes acceptance of the updated Privacy Policy.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Contact Us</h2>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices,
            please contact us at:
          </p>
          <p className="contact-info">
            <strong>DocPlix Solutions Private Limited</strong><br />
            Email: <a href="mailto:contact@docplix.com">contact@docplix.com</a>
          </p>
        </section>
      </div>

      <div className="legal-footer">
        <p>&copy; 2025 DocPlix Solutions Private Limited. All rights reserved.</p>
      </div>
    </div>
  );
}

export default PrivacyPage;
